import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Bot, Crown, Flag, Handshake, History, Inbox, LayoutDashboard, ListChecks,
  LoaderCircle, Lock, LogOut, Settings, ShieldCheck, Users,
} from "lucide-react";
import { Logo } from "@/components/landing/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { can, isAdminRole, ROLE_LABELS } from "@/lib/adminRoles";

const NAV = [
  { to: "/admin", label: "Vue d'ensemble", icon: LayoutDashboard, end: true, perm: "stats", testid: "admin-nav-overview" },
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users, perm: "users.view", testid: "admin-nav-users" },
  { to: "/admin/moderation", label: "Modération", icon: Flag, perm: "moderation", testid: "admin-nav-moderation" },
  { to: "/admin/contenus", label: "Contenus pédagogiques", icon: BookOpen, perm: "contents", testid: "admin-nav-contents" },
  { to: "/admin/questions", label: "Quiz & Examens", icon: ListChecks, perm: "quiz", testid: "admin-nav-questions" },
  { to: "/admin/ia", label: "Intelligence artificielle", icon: Bot, perm: "ai", testid: "admin-nav-ai" },
  { to: "/admin/premium", label: "Premium / Stripe", icon: Crown, perm: "premium", testid: "admin-nav-premium" },
  { to: "/admin/partenaires", label: "Partenaires", icon: Handshake, perm: "partners", testid: "admin-nav-partners", badgeKey: "partners_pending" },
  { to: "/admin/suggestions", label: "Support & Suggestions", icon: Inbox, perm: "support", testid: "admin-nav-suggestions", badgeKey: "suggestions_new" },
  { to: "/admin/journal", label: "Journal d'audit", icon: History, perm: "audit", testid: "admin-nav-audit" },
  { to: "/admin/securite", label: "Sécurité", icon: Lock, perm: "security", testid: "admin-nav-security" },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings, perm: "settings", testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!isAdminRole(user)) return;
    const load = () => api.get("/admin/pending-counts").then((r) => setPending(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user]);

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="admin-loading">
        <LoaderCircle className="h-8 w-8 animate-spin text-bleu" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;
  if (!isAdminRole(user)) return <Navigate to="/dashboard" replace />;

  const nav = NAV.filter((item) => can(user, item.perm));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[264px] flex-col bg-bleu-night lg:flex">
        <div className="px-5 pb-4 pt-6">
          <Link to="/"><Logo compact dark /></Link>
          <p className="mt-3 flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-white/80">
            <ShieldCheck className="h-4 w-4 text-bleu-accent" /> {ROLE_LABELS[user.role] || "Administration"}
          </p>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-4" aria-label="Navigation administration">
          {nav.map((item) => {
            const Icon = item.icon;
            const count = item.badgeKey ? pending[item.badgeKey] || 0 : 0;
            return (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[13px] font-medium transition-colors ${
                    isActive ? "bg-white text-bleu" : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`
                }>
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                {count > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rouge px-1.5 text-[10px] font-bold text-white"
                    data-testid={`${item.testid}-badge`}>{count}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3">
          <Link to="/" data-testid="admin-back-to-site"
            className="flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-[13px] font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Retour au site
          </Link>
          <button onClick={handleLogout} data-testid="admin-logout-button"
            className="flex w-full items-center gap-2 rounded-xl px-3.5 py-2.5 text-[13px] font-medium text-white/70 transition-colors hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" /> Se déconnecter
          </button>
        </div>
      </aside>

      <div className="lg:pl-[264px]">
        <header className="sticky top-0 z-30 border-b border-slate-100 bg-white/85 backdrop-blur-xl lg:hidden">
          <div className="flex items-center gap-2 overflow-x-auto px-4 py-3" data-testid="admin-mobile-nav">
            <Link to="/" className="flex shrink-0 items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[11px] font-bold text-slate-500">
              <ArrowLeft className="h-3 w-3" /> Site
            </Link>
            {nav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end}
                className={({ isActive }) =>
                  `shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold ${
                    isActive ? "bg-bleu text-white" : "border border-slate-200 text-slate-500"
                  }`
                }>
                {item.label}
              </NavLink>
            ))}
          </div>
        </header>
        <main className="px-4 py-7 sm:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
